import { NoddiIcon } from 'noddi-ui-common';
import { useEffect, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

export interface LightboxPhoto {
  imageUrl: string;
  label: string;
}

interface Props {
  photos: LightboxPhoto[];
  index: number;
  onIndexChange: (index: number) => void;
  onClose: () => void;
  /** Facts after the photo counter, such as the tire size. */
  details?: ReactNode;
}

/**
 * Full-screen photo viewer with previous/next and arrow-key navigation. The host owns the body
 * scroll lock and Escape (`useFullScreenBodyLock`), and mounts this only while it is open.
 */
export const PhotoLightbox = ({ photos, index, onIndexChange, onClose, details }: Props) => {
  const photo = photos[index];
  const total = photos.length;
  const canNavigate = total > 1;
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const failed = photo != null && failedUrl === photo.imageUrl;

  const goToRelative = (delta: number) => onIndexChange((index + delta + total) % total);

  useEffect(() => {
    if (!canNavigate) {
      return;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') {
        return;
      }
      event.preventDefault();
      const delta = event.key === 'ArrowLeft' ? -1 : 1;
      onIndexChange((index + delta + total) % total);
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [canNavigate, index, total, onIndexChange]);

  if (!photo) {
    return null;
  }

  return createPortal(
    // oxlint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions -- full-screen photo backdrop that closes the lightbox; the X button in its header is the keyboard path
    <div
      role='dialog'
      aria-modal
      aria-label={photo.label}
      onClick={onClose}
      className='fixed inset-0 z-fullscreen flex animate-in flex-col bg-black/90 duration-200 fade-in-0'
    >
      <div className='flex items-center justify-between gap-4 p-5 text-white'>
        <div className='flex min-w-0 flex-col'>
          <span className='text-base font-semibold'>{photo.label}</span>
          <div className='flex flex-wrap items-center gap-x-2 text-xs text-white/60'>
            {canNavigate && (
              <span>
                {index + 1} / {total}
              </span>
            )}
            {details && canNavigate && <span aria-hidden>·</span>}
            {details}
          </div>
        </div>
        <button
          type='button'
          onClick={onClose}
          aria-label='Close'
          className='flex size-10 shrink-0 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20'
        >
          <NoddiIcon name='X' size={20} />
        </button>
      </div>

      <div className='relative flex min-h-0 flex-1 items-center justify-center px-4 pb-6 md:px-16'>
        {canNavigate && (
          <button
            type='button'
            aria-label='Previous photo'
            onClick={(event) => {
              event.stopPropagation();
              goToRelative(-1);
            }}
            className='absolute left-2 z-10 flex size-10 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20 md:left-4 md:size-12'
          >
            <NoddiIcon name='ChevronLeft' size={24} />
          </button>
        )}

        {/* oxlint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- wrapper that stops a click on the photo from closing the lightbox behind it */}
        <div className='relative max-h-full max-w-full' onClick={(event) => event.stopPropagation()}>
          {failed ? (
            <div className='flex h-64 w-full max-w-lg flex-col items-center justify-center gap-4 rounded-lg bg-white/10 text-white'>
              <NoddiIcon name='CameraOff' size={32} />
              <span className='text-sm'>Photo unavailable</span>
            </div>
          ) : (
            <img
              src={photo.imageUrl}
              alt={photo.label}
              referrerPolicy='no-referrer'
              className='max-h-[calc(100dvh-8rem)] max-w-full animate-in rounded-lg object-contain shadow-2xl duration-200 zoom-in-95'
              onError={() => setFailedUrl(photo.imageUrl)}
            />
          )}
          {!failed && (
            <span className='absolute top-3 left-3 rounded-full bg-white/90 px-3 py-1 text-xs font-semibold text-black backdrop-blur-xs'>
              {photo.label}
            </span>
          )}
        </div>

        {canNavigate && (
          <button
            type='button'
            aria-label='Next photo'
            onClick={(event) => {
              event.stopPropagation();
              goToRelative(1);
            }}
            className='absolute right-2 z-10 flex size-10 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20 md:right-4 md:size-12'
          >
            <NoddiIcon name='ChevronRight' size={24} />
          </button>
        )}
      </div>
    </div>,
    document.body
  );
};
